import { gsap } from 'gsap'
import { Color } from 'three'

import { deepMerge, getCubicCurve, getTargetOffset, setElementHide } from 'js/utils/Utils'

import BasePlaneMesh from 'js/webgl/customMeshes/base/BasePlaneMesh'
import Common from 'js/_core/Common'

import VertexShader from 'js/webgl/_glsl/vert/slideshowAPlaneMesh.vert'
import FragmentShader from 'js/webgl/_glsl/frag/slideshowAPlaneMesh.frag'

import WebGLFadeSlideshow from 'js/modules/projects/ui/WebGLFadeSlideshow'

const defaults = {
  instanceName: 'slideshowAPlaneMesh',
  $names: {
    slideshowItem: '[data-slideshow-item]',
  },
  effectInfo: {
    maskStaggerDelay: 0.125,
  },
}

export default class SlideshowAPlaneMesh extends BasePlaneMesh {
  index = 0
  $slideshowItems = []
  wh = 0

  originPos = {
    x: 0,
    y: 0,
  }
  meshOffset = {
    x: 0,
    y: 0,
  }
  meshProps = {}
  textures = []

  constructor(options = {}) {
    super({
      vs: VertexShader,
      fs: FragmentShader,
    })

    this.webgl = null
    this.uniforms = this.uniforms || {}
    this.isInView = false
    this.inViewAnimated = false
    this.width = 0
    this.height = 0
    this.currentIndex = 0

    deepMerge(this, defaults, options)

    this.slideshow = new WebGLFadeSlideshow({
      mesh: this,
      $wrapper: this.$target,
      instanceName: `${this.instanceName}Slideshow` + this.index,
    })
  }

  init() {
    this._set$()
    super.init()
    this._setUniforms()
    this._setBaseSize()
    this.setResolutions()
    setElementHide(this.$target)
    this.setTexture()
    Common.webgl.stage.scene.add(this.mesh)
    this._addIoms()
    this.slideshow?.init()
  }

  destroy() {
    this._removeIoms()
    if (this.material.uniforms.uCurrentTexture && this.material.uniforms.uCurrentTexture.value) {
      this.material.uniforms.uCurrentTexture.value.dispose()
      this.material.uniforms.uCurrentTexture.value = null
    }

    if (this.mesh.material && this.mesh.material.uniforms.uCurrentTexture && this.mesh.material.uniforms.uCurrentTexture.value) {
      this.mesh.material.uniforms.uCurrentTexture.value.dispose()
      this.mesh.material.uniforms.uCurrentTexture.value = null
    }

    if (this.uniforms.uCurrentTexture && this.uniforms.uCurrentTexture.value) {
      this.uniforms.uCurrentTexture.value.dispose()
      this.uniforms.uCurrentTexture.value = null
    }

    if (this.material.uniforms.uNextTexture && this.material.uniforms.uNextTexture.value) {
      this.material.uniforms.uNextTexture.value.dispose()
      this.material.uniforms.uNextTexture.value = null
    }

    if (this.mesh.material && this.mesh.material.uniforms.uNextTexture && this.mesh.material.uniforms.uNextTexture.value) {
      this.mesh.material.uniforms.uNextTexture.value.dispose()
      this.mesh.material.uniforms.uNextTexture.value = null
    }

    if (this.uniforms.uNextTexture && this.uniforms.uNextTexture.value) {
      this.uniforms.uNextTexture.value.dispose()
      this.uniforms.uNextTexture.value = null
    }

    this.textures.forEach((texture, i) => {
      this.textures[i].dispose()
      this.textures[i] = null
    })

    this.slideshow?.destroy()

    super.destroy()
  }

  _set$() {
    if (this.$target) {
      this.$slideshowItems = this.$target.querySelectorAll(this.$names.slideshowItem)
    }
  }

  _addIoms() {
    if (Common.iom2) {
      Common.iom2.add(
        this.$target,
        () => {
          if (!this.isInView) {
            this.isInView = true
            this.inViewAnimation()
            this._removeIoms()
          }
        },
        () => {}
      )
    }
  }

  _removeIoms() {
    if (Common.iom2) {
      Common.iom2.remove(this.$target)
    }
  }

  _setUniforms() {
    deepMerge(this.uniforms, {
      uMeshOffset: {
        value: {
          x: 0,
          y: 0,
        },
      },
      uCurrentTexture: { value: null },
      uCurrentTextureResolution: {
        value: {
          x: 1,
          y: 1,
        },
      },
      uNextTexture: { value: null },
      uNextTextureResolution: {
        value: {
          x: 1,
          y: 1,
        },
      },
      uSlideshowProgress: {
        value: 0,
      },
      uInViewMono: {
        value: 1,
      },
      uInViewMask1: {
        value: 0,
      },
      uInViewMask2: {
        value: 0,
      },
      uInViewMask3: {
        value: 0,
      },
      uInViewTranslate1: {
        value: 0,
      },
      uInViewTranslate2: {
        value: 0,
      },
      uInViewTranslate3: {
        value: 0,
      },
      uInViewScale: {
        value: 1,
      },
      uBgColor: { value: new Color('#0d0d0d').convertLinearToSRGB() },
    })
  }

  setTexture() {
    this.$slideshowItems.forEach(($item, i) => {
      const texturePath = $item.dataset['glImg']

      Common.textureList['slideshowAItem'].forEach((texture) => {
        if (texture.name === texturePath) {
          if (texturePath === texture.name) {
            let width = 1
            let height = 1

            if (texture.image) {
              width = texture.image.naturalWidth
              height = texture.image.naturalHeight
            }

            this.textures[i] = texture

            if (i === 0) {
              this.uniforms.uCurrentTexture.value = this.textures[i]
              this.uniforms.uCurrentTextureResolution.value.x = width
              this.uniforms.uCurrentTextureResolution.value.y = height
            } else if (i === 1) {
              this.uniforms.uNextTexture.value = this.textures[i]
              this.uniforms.uNextTextureResolution.value.x = width
              this.uniforms.uNextTextureResolution.value.y = height
            }
          }
        }
      })
    })
  }

  inViewAnimation() {
    requestAnimationFrame(() => {
      gsap.to(this.uniforms.uInViewScale, {
        value: 0,
        ease: 'expo.out',
        duration: 3,
        delay: 0,
      })
      gsap.to(this.uniforms.uInViewMono, {
        value: 0,
        ease: 'none',
        duration: 0.4,
        delay: 0.85,
      })

      gsap.to(this.uniforms.uInViewMask1, {
        value: 1,
        ease: getCubicCurve(0.4, 0.3, 0.05, 1),
        duration: 0.85,
        delay: 0,
      })

      gsap.to(this.uniforms.uInViewMask2, {
        value: 1,
        ease: getCubicCurve(0.4, 0.3, 0.05, 1),
        duration: 0.85,
        delay: this.effectInfo.maskStaggerDelay,
      })

      gsap.to(this.uniforms.uInViewMask3, {
        value: 1,
        ease: getCubicCurve(0.4, 0.3, 0.05, 1),
        duration: 0.85,
        delay: this.effectInfo.maskStaggerDelay * 2,
        onComplete: () => {
          this.slideshow?.run()
        },
      })
      gsap.to(this.uniforms.uInViewTranslate1, {
        value: 1,
        ease: 'expo.out',
        duration: 1.5,
        delay: 0,
      })

      gsap.to(this.uniforms.uInViewTranslate2, {
        value: 1,
        ease: 'expo.out',
        duration: 1.5,
        delay: this.effectInfo.maskStaggerDelay,
      })

      gsap.to(this.uniforms.uInViewTranslate3, {
        value: 1,
        ease: 'expo.out',
        duration: 1.5,
        delay: this.effectInfo.maskStaggerDelay * 2,
      })
    })
  }

  setResolutions() {
    if (this.$target) {
      const rect = this.$target.getBoundingClientRect()
      this.width = rect.width
      this.height = rect.height

      this.uniforms.uMeshResolution.value.x = this.width
      this.uniforms.uMeshResolution.value.y = this.height

      this.originPos.x = -(Common.webgl.stage.stageSize.width * 0.5) + this.width * 0.5
      this.originPos.y = Common.webgl.stage.stageSize.height * 0.5 - this.height * 0.5

      this.mesh.scale.z = 0.0001
    }
  }

  _setPosition() {
    const offset = getTargetOffset(this.$target)

    this.meshOffset.x = offset.left
    this.meshOffset.y = offset.top

    this.uniforms.uMeshOffset.value.x = this.originPos.x + this.meshOffset.x
    this.uniforms.uMeshOffset.value.y = this.originPos.y - (this.meshOffset.y - Common.scroll.currentScroll)
  }

  _setBaseSize() {
    super._setBaseSize()
    this.wh = window.innerHeight
  }

  onResize() {
    this._setBaseSize()
    this.setResolutions()
    this._setPosition()
  }

  onRaf() {
    this._setPosition()
    if (Common.iomEnable && this.isInView) {
    }
  }
}